import React, { useEffect, useRef, useState } from 'react';
import { Dialog, Box, Typography, IconButton, Avatar, CircularProgress } from '@mui/material';
import CallEndIcon from '@mui/icons-material/CallEnd';
import CallIcon from '@mui/icons-material/Call';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';

const VoiceCallModal = ({ ws, currentEmpId, callData, onEndCall, baseUrl }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [callStatus, setCallStatus] = useState('incoming'); 
  
  const localStream = useRef(null);
  const peerConnection = useRef(null);
  
  const remoteAudioRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);

  const ringAudio = useRef(new Audio('/sounds/ringtone.mp3'));
  const dialAudio = useRef(new Audio('/sounds/dialtone.mp3'));

  useEffect(() => {
    ringAudio.current.loop = true;
    dialAudio.current.loop = true;
  }, []);

  const safePlayAudio = (audioObj) => {
    if (!audioObj) return;
    const playPromise = audioObj.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => { /* Safely swallow autoplay/missing source errors */ });
    }
  };

  const safePauseAudio = (audioObj) => {
    if (!audioObj) return;
    audioObj.pause();
    audioObj.currentTime = 0;
  };

  const safeSendWS = (payload) => {
    if (ws.current && ws.current.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify(payload));
    } else {
      console.warn("WebSocket reconnecting. Holding signal...");
      setTimeout(() => {
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
          ws.current.send(JSON.stringify(payload));
        } else {
          console.error("Connection lost completely.");
          handleHangUp(false);
        }
      }, 3500); 
    }
  };

  useEffect(() => {
    if (!callData) return;
    setCallStatus(callData.isCaller ? 'calling' : 'incoming');
    if (callData.isCaller) startWebRTC(true);
    return () => cleanupCall();
  }, [callData]);

  useEffect(() => {
    if (callStatus === 'calling') {
      safePlayAudio(dialAudio.current);
    } else {
      safePauseAudio(dialAudio.current);
    }

    if (callStatus === 'incoming') {
      safePlayAudio(ringAudio.current);
    } else {
      safePauseAudio(ringAudio.current);
    }
  }, [callStatus]);

  useEffect(() => {
    if (!callData || !callData.signalPayload) return;
    const handleSignal = async (payload) => {
      try {
        if (payload.type === 'answer' && peerConnection.current) {
          await peerConnection.current.setRemoteDescription(new RTCSessionDescription(payload.sdp));
          setCallStatus('connected');
        } 
        else if (payload.type === 'ice-candidate' && peerConnection.current) {
          await peerConnection.current.addIceCandidate(new RTCIceCandidate(payload.candidate));
        }
        else if (payload.type === 'end-call') {
          handleHangUp(false); 
        }
      } catch (err) {
        console.error("WebRTC Signaling Error:", err);
      }
    };
    handleSignal(callData.signalPayload);
  }, [callData?.signalPayload]);

  const startWebRTC = async (isCaller) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: callData.isVideo });
      localStream.current = stream;

      if (callData.isVideo && localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      const pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      stream.getTracks().forEach(track => pc.addTrack(track, stream));

      pc.ontrack = (event) => {
        if (callData.isVideo && remoteVideoRef.current) {
          remoteVideoRef.current.srcObject = event.streams[0];
        } else if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = event.streams[0];
        }
      };

      pc.onicecandidate = (event) => {
        if (event.candidate) {
          safeSendWS({
            isWebRTC: true, type: 'ice-candidate',
            target_emp_id: callData.targetId.toUpperCase(),
            sender_emp_id: currentEmpId,
            candidate: event.candidate
          });
        }
      };

      peerConnection.current = pc;

      if (isCaller) {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        safeSendWS({
          isWebRTC: true, type: 'offer',
          target_emp_id: callData.targetId.toUpperCase(),
          sender_emp_id: currentEmpId,
          caller_name: localStorage.getItem('first_name') || currentEmpId,
          isVideo: callData.isVideo,
          sdp: offer
        });
      } else if (callData.signalPayload && callData.signalPayload.sdp) {
        await pc.setRemoteDescription(new RTCSessionDescription(callData.signalPayload.sdp));
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        safeSendWS({
          isWebRTC: true, type: 'answer',
          target_emp_id: callData.targetId.toUpperCase(),
          sender_emp_id: currentEmpId,
          sdp: answer
        });
        setCallStatus('connected');
      }
    } catch (err) {
      console.error("WebRTC Error:", err);
      alert("Media access denied. Please check microphone/camera permissions and ensure you are using HTTPS or Localhost.");
      handleHangUp(true);
    }
  };

  const handleAcceptCall = () => { startWebRTC(false); };

  const handleHangUp = (notifyPeer = true) => {
    if (notifyPeer && callData?.targetId) {
      safeSendWS({
        isWebRTC: true, type: 'end-call',
        target_emp_id: callData.targetId.toUpperCase(),
        sender_emp_id: currentEmpId
      });
    }
    cleanupCall();
    onEndCall();
  };

  const cleanupCall = () => {
    safePauseAudio(ringAudio.current);
    safePauseAudio(dialAudio.current);
    if (localStream.current) localStream.current.getTracks().forEach(track => track.stop());
    if (peerConnection.current) peerConnection.current.close();
    localStream.current = null;
    peerConnection.current = null;
  };

  const toggleMute = () => {
    if (localStream.current) {
      const audioTrack = localStream.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = isMuted;
        setIsMuted(!isMuted);
      }
    }
  };

  const toggleVideo = () => {
    if (localStream.current) {
      const videoTrack = localStream.current.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = isVideoOff;
        setIsVideoOff(!isVideoOff);
      }
    }
  };

  if (!callData) return null;

  const targetProfilePic = `${baseUrl}/employees/${callData.targetId}/profile-picture?t=${Date.now()}`;
  const isVideoMode = callData.isVideo && callStatus === 'connected';

  return (
    <Dialog open={true} maxWidth={isVideoMode ? "md" : "xs"} fullWidth PaperProps={{ sx: { borderRadius: 4, bgcolor: '#202124', color: '#fff', textAlign: 'center', p: isVideoMode ? 2 : 4 } }}>
      <audio ref={remoteAudioRef} autoPlay />

      {isVideoMode ? (
        <Box sx={{ position: 'relative', height: '65vh', bgcolor: '#000', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
          <video ref={remoteVideoRef} autoPlay playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <Box sx={{ position: 'absolute', bottom: 16, right: 16, width: 140, height: 180, bgcolor: '#333', borderRadius: 2, overflow: 'hidden', border: '2px solid rgba(255,255,255,0.2)', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
             <video ref={localVideoRef} autoPlay muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </Box>
        </Box>
      ) : (
        <>
          <Avatar src={targetProfilePic} sx={{ width: 100, height: 100, mx: 'auto', mb: 2, border: '4px solid #3c4043' }}>
            {callData.name ? callData.name.charAt(0) : '?'}
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{callData.name}</Typography>
          <Typography variant="body2" sx={{ color: '#9aa0a6', mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
            {callStatus === 'calling' && <><CircularProgress size={16} color="inherit" /> Calling...</>}
            {callStatus === 'incoming' && `Incoming ${callData.isVideo ? 'Video' : 'Voice'} Call...`}
            {callStatus === 'connected' && 'Call Connected'}
          </Typography>
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
        {callStatus === 'incoming' ? (
          <>
            <IconButton onClick={() => handleHangUp(true)} sx={{ bgcolor: '#ea4335', color: '#fff', width: 56, height: 56, '&:hover': { bgcolor: '#d93025' } }}>
              <CallEndIcon />
            </IconButton>
            <IconButton onClick={handleAcceptCall} sx={{ bgcolor: '#34a853', color: '#fff', width: 56, height: 56, '&:hover': { bgcolor: '#2e964a' } }}>
              {callData.isVideo ? <VideocamIcon /> : <CallIcon />}
            </IconButton>
          </>
        ) : (
          <>
            <IconButton onClick={toggleMute} sx={{ bgcolor: isMuted ? '#fff' : '#3c4043', color: isMuted ? '#202124' : '#fff', width: 56, height: 56, '&:hover': { bgcolor: isMuted ? '#f1f3f4' : '#5f6368' } }}>
              {isMuted ? <MicOffIcon /> : <MicIcon />}
            </IconButton>
            
            {callData.isVideo && (
              <IconButton onClick={toggleVideo} sx={{ bgcolor: isVideoOff ? '#fff' : '#3c4043', color: isVideoOff ? '#202124' : '#fff', width: 56, height: 56, '&:hover': { bgcolor: isVideoOff ? '#f1f3f4' : '#5f6368' } }}>
                {isVideoOff ? <VideocamOffIcon /> : <VideocamIcon />}
              </IconButton>
            )}

            <IconButton onClick={() => handleHangUp(true)} sx={{ bgcolor: '#ea4335', color: '#fff', width: 56, height: 56, '&:hover': { bgcolor: '#d93025' } }}>
              <CallEndIcon />
            </IconButton>
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default VoiceCallModal;